/**
 * Asset Status Badge.
 *
 * Renders a colored badge for an asset's lifecycle status. Supports all
 * system lifecycle statuses with phase-appropriate colors, plus custom
 * statuses which fall back to the default style. If a hex color is
 * provided (from the custom status definition), it's used directly.
 */

import Badge from "@/components/ui/Badge";

interface AssetStatusBadgeProps {
  status: string;
  color?: string;
}

const statusVariantMap: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  // Pre-Acquisition
  Draft: "default",
  "On Order": "info",
  "In Transit": "info",

  // Intake
  Received: "info",
  "In Inspection": "warning",
  "Pending Title": "danger",

  // Reconditioning
  "Recon Scheduled": "warning",
  "In Reconditioning": "warning",
  "Recon Complete": "success",

  // Sales-Ready
  Available: "success",
  Listed: "success",
  Reserved: "info",

  // Sale Process
  "Deposit Received": "info",
  "Pending Payment": "info",
  "Pending Delivery": "info",

  // Post-Sale
  Delivered: "success",
  Sold: "success",

  // Disposition
  Wholesale: "default",
  "At Auction": "warning",
  Returned: "danger",
  "Written Off": "danger",
  Transferred: "default",
  Cancelled: "danger",
};

function AssetStatusBadge({ status, color }: AssetStatusBadgeProps) {
  // If a custom hex color is provided, render with that color directly.
  if (color) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
        style={{
          backgroundColor: `${color}20`,
          color,
          border: `1px solid ${color}40`,
        }}
      >
        {status}
      </span>
    );
  }

  const variant = statusVariantMap[status] || "default";
  return <Badge variant={variant}>{status}</Badge>;
}

export default AssetStatusBadge;
